import CommonModal from "@/common/components/common/CommonModal";
import AntDTable from "@/common/components/ui/antd-table";
import { Box, RefreshCcw, ArrowUp, ArrowDown } from "lucide-react";

interface HistoryLedgerDialogProps {
  open: boolean;
  onClose: () => void;
  selectedProduct: any;
}

const HistoryLedgerDialog = ({
  open,
  onClose,
  selectedProduct,
}: HistoryLedgerDialogProps) => {
  return (
    <CommonModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      title={
        selectedProduct ? `${selectedProduct.name} - Ledger` : "History Ledger"
      }
      width={900}
      footer={null}
    >
      {selectedProduct && (
        <>
          <div className="mb-4 text-sm text-muted-foreground">
            Track price changes and stock movement logs
          </div>

          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Stock Ledger */}
            <div className="space-y-4">
              <h4 className="flex items-center gap-2 border-b pb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                <Box className="h-3.5 w-3.5" /> Stock Movement
              </h4>
              <div className="h-64 overflow-hidden overflow-y-auto rounded-lg border">
                <AntDTable
                  scroll={{ y: 256 }}
                  pagination={false}
                  className="h-64"
                  showSettings
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "date",
                      key: "date",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-[11px]">{text}</span>
                      ),
                    },
                    {
                      title: "Change",
                      dataIndex: "change",
                      key: "change",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span
                          className={`${text.startsWith("+") ? "text-green-600" : "text-red-600"} text-[11px] font-bold`}
                        >
                          {text}
                        </span>
                      ),
                    },
                    {
                      title: "Reason",
                      dataIndex: "reason",
                      key: "reason",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-[11px] italic text-muted-foreground">
                          {text}
                        </span>
                      ),
                    },
                    {
                      title: "Balance",
                      dataIndex: "balance",
                      key: "balance",
                      align: "right",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-right text-[11px] font-bold">
                          {text}
                        </span>
                      ),
                    },
                  ]}
                  data={[
                    {
                      date: "2024-02-17",
                      change: "-2",
                      reason: "Order #FINE_001",
                      balance: "48",
                    },
                    {
                      date: "2024-02-16",
                      change: "+50",
                      reason: "Inventory Addition",
                      balance: "50",
                    },
                    {
                      date: "2024-02-10",
                      change: "+10",
                      reason: "Manual Adj",
                      balance: "0",
                    },
                  ]}
                  rowKey="date"
                />
              </div>
            </div>

            {/* Price Ledger */}
            <div className="space-y-4">
              <h4 className="flex items-center gap-2 border-b pb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                <RefreshCcw className="h-3.5 w-3.5" /> Price History
              </h4>
              <div className="h-64 overflow-hidden overflow-y-auto rounded-lg border">
                <AntDTable
                  scroll={{ y: 256 }}
                  pagination={false}
                  showSettings
                  className="h-64"
                  columns={[
                    {
                      title: "Date",
                      dataIndex: "date",
                      key: "date",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-[11px]">{text}</span>
                      ),
                    },
                    {
                      title: "Old MRP",
                      dataIndex: "oldMrp",
                      key: "oldMrp",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-[11px] text-muted-foreground line-through">
                          ₹{text}
                        </span>
                      ),
                    },
                    {
                      title: "New MRP",
                      key: "newMrp",
                      className: "text-[10px] font-bold",
                      render: (_: unknown, log: any) => (
                        <div className="flex items-center gap-1 text-[11px] font-bold">
                          ₹{log.newMrp}
                          {log.trend === "up" ? (
                            <ArrowUp className="h-2 w-2 text-red-500" />
                          ) : (
                            <ArrowDown className="h-2 w-2 text-green-500" />
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Selling",
                      dataIndex: "selling",
                      key: "selling",
                      align: "right",
                      className: "text-[10px] font-bold",
                      render: (text: string) => (
                        <span className="text-right text-[11px] font-black text-secondary">
                          ₹{text}
                        </span>
                      ),
                    },
                  ]}
                  data={[
                    {
                      date: "2024-02-17",
                      oldMrp: "10390",
                      newMrp: "10450",
                      selling: "10250",
                      trend: "up",
                    },
                    {
                      date: "2024-02-16",
                      oldMrp: "10200",
                      newMrp: "10390",
                      selling: "10100",
                      trend: "up",
                    },
                    {
                      date: "2024-02-15",
                      oldMrp: "10250",
                      newMrp: "10200",
                      selling: "10050",
                      trend: "down",
                    },
                  ]}
                  rowKey="date"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </CommonModal>
  );
};

export default HistoryLedgerDialog;
